import clsx from 'clsx';
import { useFormik } from 'formik';
import { FC, useEffect } from 'react';
import * as Yup from 'yup';
import { SubscriptionModalState, SubscriptionType } from '../../model/types';
import {
	useCreateSubscription,
	useDeleteSubscription,
	useUpdateSubscription,
} from '../api/subscription.mutations';

// Обновленная схема валидации под новую структуру
const subscriptionSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, 'Минимум 3 символа')
		.max(50, 'Максимум 50 символов')
		.required('Введите название'),
	type: Yup.string().required('Выберите тип'),
	description: Yup.string().required('Введите описание'),
	price: Yup.number()
		.min(0, 'Цена не может быть меньше 0')
		.required('Укажите цену'),
	duration_days: Yup.number()
		.min(1, 'Минимум 1 день')
		.required('Укажите длительность'),
});

interface Props {
	openModal: SubscriptionModalState;
	handleModalClose: () => void;
	setShowErrorToast: (state: boolean) => void;
}

const SubscriptionModalForm: FC<Props> = ({
	openModal,
	handleModalClose,
	setShowErrorToast,
}) => {
	const { mutateAsync: createSubscription } = useCreateSubscription();
	const { mutateAsync: updateSubscription } = useUpdateSubscription();
	const { mutateAsync: deleteSubscription } = useDeleteSubscription();

	const isDelete = openModal.type === 'delete';

	const formik = useFormik({
		initialValues: {
			name: '',
			type: 'PAID' as SubscriptionType,
			description: '',
			price: 0,
			duration_days: 30,
			// Новый тариф включён: заводят его, чтобы продавать, а не чтобы
			// он полежал.
			active: true,
		},
		validationSchema: !isDelete ? subscriptionSchema : null,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				const { price, ...rest } = values;
				const payload = { ...rest, price_cents: Math.round(price * 100) };
				if (openModal.type === 'create') {
					await createSubscription(payload);
				} else if (openModal.type === 'edit' && openModal.data) {
					await updateSubscription({
						uuid: openModal.data.uuid,
						request: payload,
					});
				} else if (openModal.type === 'delete' && openModal.data) {
					await deleteSubscription(openModal.data.uuid);
				}
				handleModalClose();
			} catch (ex) {
				setShowErrorToast(true);
				console.error(ex);
			} finally {
				setSubmitting(false);
			}
		},
	});

	useEffect(() => {
		if (
			(openModal.type === 'edit' || openModal.type === 'delete') &&
			openModal.data
		) {
			formik.setValues({
				name: openModal.data.name || '',
				type: openModal.data.type || 'PAID',
				description: openModal.data.description || '',
				price: (openModal.data.price_cents || 0) / 100,
				duration_days: openModal.data.duration_days || 0,
				active: openModal.data.active ?? true,
			});
		} else if (openModal.type === 'create') {
			formik.resetForm();
		}
	}, [openModal]);

	return (
		<form onSubmit={formik.handleSubmit} noValidate className='form'>
			<div className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'>
				{!isDelete ? (
					<>
						{/* Название */}
						<div className='fv-row mb-7'>
							<label className='required fw-bold fs-6 mb-2'>
								Название подписки
							</label>
							<input
								{...formik.getFieldProps('name')}
								className={clsx('form-control form-control-solid', {
									'is-invalid': formik.touched.name && formik.errors.name,
								})}
								disabled={formik.isSubmitting}
							/>
							{formik.touched.name && formik.errors.name && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>{formik.errors.name}</div>
								</div>
							)}
						</div>

						{/* Тип (Select) */}
						<div className='fv-row mb-7'>
							<label className='required fw-bold fs-6 mb-2'>Тип</label>
							<select
								{...formik.getFieldProps('type')}
								className='form-select form-select-solid'
								disabled={formik.isSubmitting}
							>
								<option value='PAID'>Платная</option>
								<option value='FREE'>Бесплатная</option>
							</select>
						</div>

						{/* Описание */}
						<div className='fv-row mb-7'>
							<label className='required fw-bold fs-6 mb-2'>Описание</label>
							<input
								{...formik.getFieldProps('description')}
								className={clsx('form-control form-control-solid', {
									'is-invalid': formik.touched.description && formik.errors.description,
								})}
								disabled={formik.isSubmitting}
							/>
							{formik.touched.description && formik.errors.description && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>{formik.errors.description}</div>
								</div>
							)}
						</div>

						<div className='row'>
							{/* Цена в долларах */}
							<div className='col-6 fv-row mb-7'>
								<label className='required fw-bold fs-6 mb-2'>Цена ($)</label>
								<input
									type='number'
									step='0.01'
									{...formik.getFieldProps('price')}
									className={clsx('form-control form-control-solid', {
										'is-invalid': formik.touched.price && formik.errors.price,
									})}
									disabled={formik.isSubmitting}
								/>
							</div>

							<div className='col-6 fv-row mb-7'>
								<label className='required fw-bold fs-6 mb-2'>
									Длительность (дни)
								</label>
								<input
									type='number'
									{...formik.getFieldProps('duration_days')}
									className={clsx('form-control form-control-solid', {
										'is-invalid':
											formik.touched.duration_days &&
											formik.errors.duration_days,
									})}
									disabled={formik.isSubmitting}
								/>
							</div>

							{/* «Статус» в таблице — это он. Выключенный тариф пропадает
							    из выдачи продавцам и его нельзя купить: покупка
							    проверяет active отдельно. То есть это выключатель
							    продажи, а не просто пометка в списке.

							    Менять его было нельзя только потому, что поля не было
							    в форме — обе ручки, и создания и обновления, его
							    принимают. */}
							<div className='col-12 fv-row mb-7'>
								<label className='form-check form-switch form-check-custom form-check-solid'>
									<input
										type='checkbox'
										className='form-check-input'
										checked={formik.values.active}
										onChange={e =>
											formik.setFieldValue('active', e.target.checked)
										}
										disabled={formik.isSubmitting}
									/>
									<span className='form-check-label fw-semibold text-gray-700'>
										Доступен продавцам
									</span>
								</label>
								<div className='text-muted fs-8 mt-1'>
									{formik.values.active
										? 'Тариф показывается в списке и его можно купить'
										: 'Тариф скрыт из списка, купить его нельзя'}
								</div>
							</div>
						</div>
					</>
				) : (
					<div className='fs-6 text-gray-700'>
						Вы уверены, что хотите удалить подписку{' '}
						<strong>{openModal.data?.name}</strong>?
					</div>
				)}
			</div>

			<div className='text-center mt-5'>
				<button
					type='button'
					onClick={handleModalClose}
					className='btn btn-light me-3'
					disabled={formik.isSubmitting}
				>
					Отмена
				</button>
				<button
					type='submit'
					className={clsx('btn', isDelete ? 'btn-danger' : 'btn-primary')}
					disabled={formik.isSubmitting || (!isDelete && !formik.isValid)}
				>
					{formik.isSubmitting
						? 'Подождите...'
						: isDelete
						? 'Удалить'
						: 'Сохранить'}
				</button>
			</div>
		</form>
	);
};

export { SubscriptionModalForm };
